import { Resources } from 'constants/resources';

import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import { usePermissions } from 'hooks/usePermissions';
import { useCreateGatewayConversionSettings } from 'pages/Monitoring/OrderConversion/OrderConversionList/hooks/useCreateGatewayConversionSettings';
import { OrderConversionListGatewaySettingsAlert } from 'pages/Monitoring/OrderConversion/OrderConversionList/OrderConversionListGateway/components/OrderConversionListGatewaySettingsAlert';
import { OrderConversionListGatewaySettingsForm } from 'pages/Monitoring/OrderConversion/OrderConversionList/OrderConversionListGateway/components/OrderConversionListGatewaySettingsForm';
import { useOrderConversionGatewaySchema } from 'pages/Monitoring/OrderConversion/OrderConversionList/OrderConversionListGateway/hooks/useOrderConversionGatewaySchema';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { queryClient } from 'providers/queryClient';
import { useNotify } from 'react-admin';
import { FormProvider, useForm } from 'react-hook-form';
import { Button, SaveButton } from 'shared/mui/Buttons';
import { CardActionsButtons } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { CardHeader } from 'shared/mui/Card/CardHeader/CardHeader';
import { Dialog } from 'shared/mui/Dialogs';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { GatewayConversionListItem } from 'types/embedded/orderConversion';
import { Permission } from 'types/embedded/permission';
import { convertReactAdminIdToHydraId } from 'utils';

type Props = {
  record: GatewayConversionListItem | undefined;
  merchant: string | null;
  open: boolean;
  onClose: VoidFunction;
  onOpenReferenceDialog: VoidFunction;
};

export type GatewaySettingsFormValues = {
  successDeviation: number;
  cancelDeviation: number;
  pendingDeviation: number;
  minOrdersCount: number;
  notify: boolean;
};

export const OrderConversionListGatewayCreate: React.FC<Props> = ({
  open,
  onClose,
  record,
  merchant,
  onOpenReferenceDialog,
}) => {
  const translate = useTypedTranslate();
  const { hasResourcePermissions } = usePermissions();
  const notify = useNotify();
  const schema = useOrderConversionGatewaySchema();

  const form = useForm<GatewaySettingsFormValues>({
    resolver: yupResolver<any>(schema),
    shouldUnregister: true,
  });

  const { createGatewayConversionSettings } = useCreateGatewayConversionSettings();
  async function onSubmit(values: GatewaySettingsFormValues) {
    if (!merchant || !record?.id) {
      return;
    }

    try {
      await createGatewayConversionSettings.mutateAsync({
        merchant,
        gateway: convertReactAdminIdToHydraId(record?.id, Resources.Gateways),
        successDeviation: values.successDeviation.toString(),
        cancelDeviation: values.cancelDeviation.toString(),
        pendingDeviation: values.pendingDeviation.toString(),
        minOrdersCount: Number(values?.minOrdersCount),
        notify: values.notify,
      });

      await queryClient.refetchQueries({
        queryKey: [Resources.OrderConversion],
      });

      onClose();
      notify(translate('app.orderConversion.settingsCreatedSuccessfully'), { type: 'success' });
    } catch (e) {
      const error = e as AxiosError<{ detail: string }>;

      notify(
        `${translate('app.notification.error')}: ${error?.response?.data?.detail || translate('app.notification.unknownError')}`,
        { type: 'error' },
      );
    }
  }

  return (
    <Dialog maxWidth="md" onClose={onClose} open={open}>
      <CardHeader
        appendActions={<CloseIconButton key="close-button" onClose={onClose} />}
        sx={{ height: 'auto' }}
        title={translate('app.orderConversion.createThresholdSettingsForMonitoring')}
        titleSize="smallBold"
      />
      <CardContent sx={{ width: '640px' }}>
        <OrderConversionListGatewaySettingsAlert onClick={onOpenReferenceDialog} />
      </CardContent>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <CardContent sx={{ width: '640px' }}>
            <OrderConversionListGatewaySettingsForm />
          </CardContent>
          <CardActionsButtons
            rightActionsSlot={[
              <Button
                key="close-button"
                label={translate('app.buttons.cancel')}
                onClick={onClose}
                variant="outlined"
              />,
              hasResourcePermissions('gateway_conversion_settings', [
                Permission.Full,
                Permission.Create,
              ]) && <SaveButton key="save-button" label={translate('app.buttons.create')} />,
            ]}
          />
        </form>
      </FormProvider>
    </Dialog>
  );
};
